import { Group } from '@visx/group';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { BarRounded } from '@visx/shape';
import ColumnChartImage from '../../components/ChartsImages/ColumnChartImage';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { Grid } from '@visx/grid';
import ChartTooltip from '../../components/ChartTooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import { getColors, MAP_CHART_FIELDS_MAX, useWindowSize } from '../../utils';
import styled from 'styled-components';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';

const StyledColumnChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type ColumnChartProps = {
  margin: any;
  data: any[];
  axesProps: any;
  tickProps?: any;
  color: string;
  emptyDataMessage: string;
  showValuesOn100: boolean;
  limit: any;
  showItemsPreview?: boolean;
  showItemsLabel?: boolean;
  label: string;
  tooltipContent: (_d) => any;
};

let tooltipTimeout: number;
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };

const ColumnChart: FC<ColumnChartProps> = ({
  data,
  margin = defaultMargin,
  axesProps = {},
  tickProps,
  color,
  emptyDataMessage,
  showValuesOn100,
  limit = 'auto',
  showItemsPreview,
  showItemsLabel,
  label,
  tooltipContent
}) => {
  const { yAxis, zAxis } = axesProps;
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const isEmpty = !data || data.length === 0;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    return limit !== 'auto' ? data.slice(0, limit) : data;
  }, [isEmpty, data, limit]);

  const yValues =
    (filteredData && filteredData.map((item: any) => item[yAxis])) || [];
  const zValues =
    (filteredData && filteredData.map((item: any) => item[zAxis])) || [];

  const minMaxValues = useMemo(() => {
    return {
      yMin: zAxis || showValuesOn100 ? 0 : Math.min(...yValues),
      yMax: Math.max(...yValues),
      zMin: zAxis || showValuesOn100 ? Math.min(...zValues) : 0,
      zMax: zAxis || showValuesOn100 ? Math.max(...zValues) : 0
    };
  }, [yAxis, yValues, zValues]);

  const yzDiff = Math.max(
    minMaxValues.yMax - minMaxValues.yMin,
    minMaxValues.zMax - minMaxValues.zMin,
    0
  );
  const yzMin = Math.min(minMaxValues.yMin, minMaxValues.zMin);
  const yzMax = Math.max(minMaxValues.yMax, minMaxValues.zMax);
  const yDomain = useMemo(
    () => [
      yzMin - yzDiff / 3 < 0 ? 0 : yzMin - yzDiff / 3,
      MAP_CHART_FIELDS_MAX[yAxis] &&
      (showValuesOn100 || yzMax + yzDiff / 3 > MAP_CHART_FIELDS_MAX[yAxis])
        ? MAP_CHART_FIELDS_MAX[yAxis]
        : yzMax + yzDiff / 3
    ],
    [minMaxValues, yzDiff, showValuesOn100, yzMin, yzMax, yAxis]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: yDomain,
        range: [0, showValuesOn100 || yzMax > 1 ? 1 : yzMax],
        nice: true
      }),
    [yzMax, yDomain, showValuesOn100]
  );

  const xScale = useMemo(
    () =>
      scaleBand({
        domain: (filteredData && filteredData.map((item) => item.id)) || [],
        padding: 0.7
      }),
    [filteredData]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [
          0,
          isEmpty ? 0 : filteredData.length / 2,
          isEmpty ? 0 : filteredData.length
        ],
        range: getColors(color)
      }),
    [filteredData, isEmpty, color]
  );

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  xScale.rangeRound([0, xMax]);
  yScale.rangeRound([yMax, 0]);

  const getTickInsightType = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.insight_type) || '';
    },
    [filteredData]
  );

  const getTickImage = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.pic) || '';
    },
    [filteredData]
  );

  const getTickLabel = useCallback(
    (d) => {
      const item = filteredData.find((i) => i.id === d);
      return (item && item.label) || '';
    },
    [filteredData]
  );

  const getGroupContent = useCallback((group, groups, index) => {
    const options = groups.tickLabelProps(group.value);
    const props = {
      ...options,
      x: group.to.x,
      y: 10 + group.to.y,
      index
    };

    return groups.tickComponent(props);
  }, []);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...filteredData[index],
          color: filteredData[index].color || colorScale(index)
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [svgRef, showTooltip, filteredData, colorScale]
  );

  const getTickComponent = useCallback(
    (d) => {
      const index = filteredData.findIndex((i) => i.id === d.id);
      return (
        <ColumnChartImage
          key={`column-${chartId.current}-category-${d.id}`}
          d={{ ...d, index }}
          color={d.color || colorScale(d.index)}
          showItemsPreview={showItemsPreview}
          showItemsLabel={showItemsLabel}
          chartId={chartId.current}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      );
    },
    [
      showItemsPreview,
      filteredData,
      chartId.current,
      colorScale,
      handleMouseMove,
      handleMouseLeave
    ]
  );

  if (isEmpty) {
    return (
      <StyledColumnChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledColumnChartWrapper>
    );
  }

  return (
    <StyledColumnChartWrapper ref={chartRef}>
      <svg
        ref={svgRef}
        width={width}
        height={showItemsLabel ? height + 50 : height}
      >
        <rect
          width={width}
          height={showItemsLabel ? height + 50 : height}
          fill='#ffffff'
          rx={14}
        />
        <Grid
          top={margin.top}
          left={margin.left}
          xScale={xScale}
          yScale={yScale}
          width={xMax}
          height={yMax}
        />
        <Group
          top={margin.top}
          left={margin.left}
          key={`group-${chartId.current}`}
        >
          {filteredData.map((d, index) => {
            const barWidth = xScale.bandwidth();
            const barZWidth = xScale.bandwidth() + 20;
            const barValue = d[yAxis] || 0;
            const barHeight = yzMax > 0 ? yMax - (yScale(barValue) ?? 0) : 0;
            const barZHeight = yzMax > 0 ? yMax - (yScale(d[zAxis]) ?? 0) : 0;
            const barX: null | any = xScale(d.id);
            const barY = yMax - barHeight;
            const barZ = yMax - barZHeight;

            return (
              <g key={`column-${chartId.current}-bar-${d.id}`}>
                {barX > 0 && barY >= 0 && barHeight >= 0 && (
                  <BarRounded
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={d.color || colorScale(index)}
                    fillOpacity={zAxis ? 0.7 : 1}
                    radius={barWidth / 4}
                    all
                    onMouseMove={(event) => handleMouseMove(event, index)}
                    onMouseLeave={(event) => handleMouseLeave(event)}
                  />
                )}
                {barX > 0 && barZ > 0 && (
                  <BarRounded
                    x={barX - 10}
                    y={barZ}
                    all
                    radius={barZWidth / 4}
                    width={barZWidth}
                    height={barZHeight}
                    fill={d.color || colorScale(index)}
                    fillOpacity={0.4}
                    onMouseMove={(event) => handleMouseMove(event, index)}
                    onMouseLeave={(event) => handleMouseLeave(event)}
                  />
                )}
              </g>
            );
          })}
          <AxisLeft
            label={label}
            scale={yScale}
            stroke='#9493A6'
            tickStroke='#9493A6'
            tickFormat={(d: any) => tickProps && tickProps.format(d, yAxis)}
            tickLabelProps={() => ({
              fill: '#9493A6',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
              dx: '-0.33em',
              fontWeight: 'bold'
            })}
            labelProps={{
              fill: 'black',
              textAnchor: 'middle',
              fontSize: 14,
              fontWeight: 'bold',
              dx: '-1.2em'
            }}
          />
          <AxisBottom
            top={yMax}
            scale={xScale}
            tickValues={xScale.domain()}
            tickLabelProps={(d) => ({
              fontSize: '11px',
              pic: getTickImage(d),
              id: d,
              label: getTickLabel(d),
              insight_type: getTickInsightType(d)
            })}
            tickComponent={(d) => getTickComponent(d)}
          >
            {(axisGroups) => (
              <React.Fragment>
                {axisGroups.ticks.map((group, index) => {
                  return (
                    <Group
                      key={`column-${chartId.current}-axis-group-${index}`}
                    >
                      {getGroupContent(group, axisGroups, index)}
                    </Group>
                  );
                })}
              </React.Fragment>
            )}
          </AxisBottom>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {tooltipContent && tooltipContent(tooltipData)}
        </ChartTooltip>
      )}
    </StyledColumnChartWrapper>
  );
};
export default ColumnChart;
